/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TemplateLayout.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, List } from "@mui/material";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import AddButtonV2 from "../../../components/customComponentsV2/common/button/AddButtonV2";

function TemplateLayout({ breadcrumbs, addButtonConfig, children, open }) {
  const formRef = useRef(null);

  useEffect(() => {
    if (open === "10") {
      const timeout = setTimeout(() => {
        if (formRef.current) {
          formRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  return (
    <List
      sx={{
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        padding: ".5rem 1rem 1rem 1rem",
        backgroundColor: "whitesmoke",
        display: "flex",
        flexDirection: "column",
      }}
      component="nav"
    >
      <Box ref={formRef} sx={{ height: "1px" }} />

      <CommonBreadcrumbs items={breadcrumbs} />

      <List
        component="div"
        disablePadding
        sx={{
          margin: "10px",
          padding: "10px",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {addButtonConfig?.show && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              mr: 3,
            }}
          >
            <AddButtonV2
              handleOnClick={addButtonConfig?.onClick}
              icon={addButtonConfig?.icon}
              label={addButtonConfig?.label}
            />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            scrollbarColor: "#888 #f1f1f1",
            scrollbarWidth: "thin",
            width: "100%",
            padding: { xs: "0 8px", md: "0 16px" },
          }}
        >
          {children}
        </Box>
      </List>
    </List>
  );
}

TemplateLayout.propTypes = {
  breadcrumbs: PropTypes.array,
  addButtonConfig: PropTypes.shape({
    show: PropTypes.bool,
    onClick: PropTypes.func,
    icon: PropTypes.node,
    label: PropTypes.string,
  }),
  children: PropTypes.node,
  open: PropTypes.string,
};

export default TemplateLayout;
